import {
    Box,
    Button,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
} from '@mui/material';
import { type ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { isGridRectangleSelectable } from '~/engine/tool/tools/contouring-tools/obliqueGating';
import { useClearContourExtent } from '~/hooks/structure-set/useClearContourExtent';
import { useShownSlicePlaneOrRefusal } from '~/hooks/structure-set/useShownSlicePlaneOrRefusal';
import { structureWriteInFlightState } from '~/state/atoms/contourDeletionAtoms';
import { brushOptionsState, selectedToolState } from '~/state/atoms/toolsAtoms';
import BrushToolOptions from './BrushToolOptions';
import RectangleEraseToolOptions from './RectangleEraseToolOptions';

// ── The eraser's shapes ──────────────────────────────────────────────
//
// Erasing by brush and erasing whole curves with a rectangle are one
// tool with two shapes, not two tools: both take contour away from the
// selected structure, and the choice between them is which shape does
// it.
//
// The brush shape IS `brush` with `erase` on; the rectangle shape is its
// own tool, because its drag is resolved against the ROI's grid rather
// than painted. The toggle hides that split — which is a fact about the
// engine, not about what the clinician is doing.

/** Wording shared with the viewport menu: one operation, one report. */
const CLEAR_MESSAGES = {
    successMessageId: 'contourDeletion.extent.success',
    failedMessageId: 'contourDeletion.extent.failed',
    undoLabelId: 'contourDeletion.undo',
    undoUnavailableMessageId: 'contourDeletion.undoUnavailable',
    logLabel: 'Delete contours',
} as const;

export type EraserShape = 'brush' | 'rectangle';

interface EraserToolOptionsProps {
    /** Switches shown after the shape's own options' switches. */
    children?: ReactNode;
}

export default function EraserToolOptions({
    children,
}: EraserToolOptionsProps) {
    const intl = useIntl();
    const [selectedTool, setSelectedTool] = useRecoilState(selectedToolState);
    const setBrushOptions = useRecoilState(brushOptionsState)[1];
    const { environment } = useActiveEditTarget();
    const { plane, readPlane, refusalMessageId } =
        useShownSlicePlaneOrRefusal('clearStructure');
    const isWriting = useRecoilValue(structureWriteInFlightState);
    const { selectedStructureSet, selectedRoiNumber } =
        useStructureToolGating();
    const { clearContourExtent } = useClearContourExtent(
        selectedStructureSet?.dicomUid,
        CLEAR_MESSAGES
    );

    const shape: EraserShape =
        selectedTool === 'rectangle-select' ? 'rectangle' : 'brush';

    // The rectangle reduces its drag to an axis-aligned WORLD box, so the
    // target ROI's own sampling grid must have a grid axis along every
    // patient axis for that box to describe voxels. Read off the grid
    // rather than the per-slice DICOM tilt flag, which a rotated
    // acquisition can satisfy while still failing this.
    const rectangleGrid =
        environment?.structureSet?.getRoi(selectedRoiNumber)?.texture
            ?.textureGrid;
    const rectangleGeometryRefuses =
        !rectangleGrid || !isGridRectangleSelectable(rectangleGrid);

    // Disarms a rectangle whose geometry turns out to refuse it — the
    // selection moved to an obliquely sampled structure while the tray
    // stayed open. Disabling the button alone would freeze it in the
    // rectangle shape over a gesture whose every drag is refused.
    useEffect(() => {
        if (rectangleGeometryRefuses && selectedTool === 'rectangle-select') {
            setBrushOptions((options) => ({ ...options, erase: true }));
            setSelectedTool('brush');
        }
    }, [
        rectangleGeometryRefuses,
        selectedTool,
        setSelectedTool,
        setBrushOptions,
    ]);

    const handleShapeChange = (
        _event: React.MouseEvent<HTMLElement>,
        value: EraserShape | null
    ) => {
        // ToggleButtonGroup emits null when the active button is
        // re-clicked; ignore it so a shape is always selected.
        if (value === null || value === shape) {
            return;
        }
        if (value === 'rectangle') {
            setSelectedTool('rectangle-select');
            return;
        }
        // Back to the brush with erase still on — the tray belongs to the
        // eraser, so leaving the rectangle must not leave a DRAWING brush
        // armed over a structure the user was taking contour off.
        setBrushOptions((options) => ({ ...options, erase: true }));
        setSelectedTool('brush');
    };

    // The edit target's slice, offered only while a tile of it shows that
    // slice: none on a tilted series, with every tile turned off the slices,
    // or with no tile of the target on the grid.
    const sliceResolves = plane !== null;

    // SRV-WOR-H-24-1-2 requires the reason wherever a delete control is
    // unavailable: a grey button the user cannot account for is one they
    // cannot tell from a defect. The refusals are kept apart because they
    // have different remedies: an obliquely sampled series needs a
    // reformat, a view that is merely not on a slice needs turning.
    const rectangleReason = rectangleGeometryRefuses
        ? intl.formatMessage({ id: 'postProcessing.delete.obliqueThisSlice' })
        : '';
    const clearSliceReason = sliceResolves
        ? ''
        : intl.formatMessage({ id: refusalMessageId });

    const handleClearSlice = () => {
        // Read at the click, never captured at render: a scroll between the
        // two must not clear the slice shown earlier.
        void clearContourExtent({
            scope: 'this-slice',
            plane: readPlane(),
            addressedBy: 'acquisition',
            offerUndo: true,
        });
    };

    return (
        <>
            <ToggleButtonGroup
                value={shape}
                exclusive
                onChange={handleShapeChange}
                size="small"
                sx={{ flexShrink: 0 }}
                aria-label={intl.formatMessage({ id: 'eraser.shape' })}
            >
                <ToggleButton value="brush" data-testid="eraser-shape-brush">
                    <FormattedMessage id="eraser.shape.brush" />
                </ToggleButton>
                {/* The span carries the tooltip: a disabled button fires
                    no pointer events, so the reason would never show. */}
                <Tooltip title={rectangleReason}>
                    <span style={{ display: 'inline-flex' }}>
                        <ToggleButton
                            value="rectangle"
                            disabled={rectangleGeometryRefuses}
                            data-testid="eraser-shape-rectangle"
                        >
                            <FormattedMessage id="eraser.shape.rectangle" />
                        </ToggleButton>
                    </span>
                </Tooltip>
            </ToggleButtonGroup>
            {shape === 'brush' ? (
                <BrushToolOptions>{children}</BrushToolOptions>
            ) : (
                <>
                    <RectangleEraseToolOptions />
                    {children}
                </>
            )}
            {/* Not a shape: it takes the whole slice at once rather than
                what a gesture covers, so it commits on the press and its
                report carries the way back. Held while no tile of the
                edit target shows its slice. */}
            <Tooltip title={clearSliceReason}>
                <Box
                    component="span"
                    sx={{ display: 'flex', flex: '1 1 100%' }}
                >
                    <Button
                        size="small"
                        variant="outlined"
                        fullWidth
                        onClick={handleClearSlice}
                        disabled={!sliceResolves || isWriting}
                        data-testid="eraser-clear-slice"
                    >
                        <FormattedMessage id="eraser.clearSlice" />
                    </Button>
                </Box>
            </Tooltip>
        </>
    );
}
